import { z } from 'zod';
import { entityNameSchema } from './common.js';

export const syncOperationSchema = z.enum(['insert', 'update', 'delete']);

/** Operacion encolada en el outbox local. Idempotente por `opId`. */
export const outboxOpSchema = z.object({
  opId: z.uuid(),
  entity: entityNameSchema,
  entityId: z.uuid(),
  operation: syncOperationSchema,
  payload: z.unknown(),
  baseRev: z.number().int().nonnegative(),
  createdAt: z.iso.datetime({ offset: true }),
  attempts: z.number().int().nonnegative(),
  lastError: z.string().nullable(),
  /**
   * `undeliverable` es distinto de `failed`, y la diferencia importa.
   *
   * `failed` es «no ha entrado TODAVIA»: se reintenta, y a los cinco intentos
   * se aparca a la espera de que alguien pulse reintentar. `undeliverable` es
   * «no va a entrar NUNCA»: el servidor ha dicho por que con un codigo de
   * PostgreSQL que no depende del momento —una clave ajena a un padre que ya
   * no existe, por ejemplo—, y reintentarlo solo gasta bateria y tapa la cola
   * buena detras de un numero que no baja.
   *
   * No se borran solas: el usuario las ve, con su motivo, y decide.
   */
  status: z.enum(['pending', 'in_flight', 'failed', 'undeliverable']),
});

export type OutboxOp = z.infer<typeof outboxOpSchema>;
export type SyncOperation = z.infer<typeof syncOperationSchema>;

export const pushResultSchema = z.object({
  /** Han ESCRITO. El cliente las saca del outbox. */
  applied: z.array(z.uuid()),
  /**
   * No han escrito, y no es un error: el servidor tiene una version que gana
   * (migracion 0026).
   *
   * Antes venian dentro de `applied`, porque el `on conflict … where` que no se
   * cumple no escribe y tampoco falla. La respuesta decia «aplicada» de dos
   * cosas distintas y el cliente no podia separarlas.
   *
   * El cliente las saca del outbox IGUAL —reintentarlas las descartaria otra
   * vez, porque la version enviada no crece sola— pero las cuenta y las
   * registra en vez de darlas por enviadas.
   *
   * Lleva `.default([])` para poder hablar con un servidor anterior a la 0026:
   * sin la clave, cero superadas, que es lo que ese servidor sabe decir.
   */
  superseded: z.array(z.uuid()).default([]),
  /** Han dado error. Siguen en el outbox y se reintentan. */
  rejected: z.array(
    z.object({
      opId: z.uuid(),
      reason: z.string(),
    }),
  ),
  /** Filas resultantes en servidor, para reconciliar el estado local. */
  rows: z.array(
    z.object({
      entity: entityNameSchema,
      row: z.record(z.string(), z.unknown()),
    }),
  ),
});

export type PushResult = z.infer<typeof pushResultSchema>;

export const pullResultSchema = z.object({
  cursor: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  rows: z.array(
    z.object({
      entity: entityNameSchema,
      row: z.record(z.string(), z.unknown()),
    }),
  ),
});

export type PullResult = z.infer<typeof pullResultSchema>;

export const syncStatusSchema = z.enum([
  'synced',
  'pending',
  'syncing',
  'offline',
  'error',
  'conflict',
]);

export type SyncStatus = z.infer<typeof syncStatusSchema>;
